import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PencilSquareIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { fournisseurService, documentService, supplierPortalAdminService, ArticleFournisseur } from '../../api/services';
import { Document, Fournisseur } from '../../types';
import { formatTND, formatDate } from '../../utils/formatters';
import { StatusBadge } from '../../components/common/StatusBadge';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

const FournisseurWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [fournisseur, setFournisseur] = useState<Fournisseur | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [catalogue, setCatalogue] = useState<ArticleFournisseur[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'factures' | 'catalogue'>('factures');

  useEffect(() => {
    if (!id) return;
    const fid = Number(id);
    Promise.all([
      fournisseurService.getById(fid),
      documentService.getByFournisseur(fid),
      fournisseurService.articles(fid),
    ])
      .then(([f, docs, cat]) => { setFournisseur(f); setDocuments(docs); setCatalogue(cat); })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [id]);

  // Factures d'achat uniquement pour les KPI (un fournisseur peut aussi avoir des bons de commande).
  const factures = useMemo(
    () => documents.filter((d) => d.typeDocument === 'FACTURE'),
    [documents],
  );
  const totalAchatsHT = useMemo(() => factures.reduce((s, d) => s + Number(d.totalHT || 0), 0), [factures]);
  const totalAPayer = useMemo(
    () => factures.reduce((s, d) => s + Number(d.resteAPayer || 0), 0),
    [factures],
  );

  if (loading) return <LoadingSpinner />;
  if (!fournisseur) return <p className="text-sm text-gray-500">Fournisseur introuvable.</p>;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/fournisseurs')} className="flex items-center text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="h-4 w-4 mr-1" /> Fournisseurs
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="page-title">{fournisseur.raisonSociale}</h1>
          <p className="page-subtitle">
            {fournisseur.matriculeFiscal ? `MF: ${fournisseur.matriculeFiscal}` : 'Fournisseur'}
            {fournisseur.ville ? ` · ${fournisseur.ville}` : ''}
            {fournisseur.telephone ? ` · ${fournisseur.telephone}` : ''}
          </p>
        </div>
        <button onClick={() => navigate(`/fournisseurs/${fournisseur.id}/edit`)} className="btn-secondary">
          <PencilSquareIcon className="h-4 w-4 mr-1" /> Modifier
        </button>
        <button
          onClick={async () => {
            const info = await supplierPortalAdminService.generateToken(fournisseur.id);
            const url = `${window.location.origin}/portail-fournisseur/${info.token}`;
            try { await navigator.clipboard?.writeText(url); } catch { /* clipboard indisponible */ }
            toast.success(`Lien portail copié : ${url}`);
          }}
          className="btn-secondary"
        >
          Lien portail
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-gray-500">Factures d'achat</p>
          <p className="text-2xl font-bold text-gray-900">{factures.length}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500">Total achats HT</p>
          <MoneyDisplay amount={totalAchatsHT} size="lg" />
        </div>
        <div className={`card ${totalAPayer > 0 ? 'ring-1 ring-amber-200' : ''}`}>
          <p className="text-sm text-gray-500">Dette (reste à payer)</p>
          <MoneyDisplay amount={totalAPayer} size="lg" className={totalAPayer > 0 ? 'text-amber-700' : ''} />
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 border-b border-gray-200">
        {(['factures', 'catalogue'] as const).map((tk) => (
          <button key={tk} onClick={() => setTab(tk)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === tk ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {tk === 'factures' ? 'Achats' : 'Catalogue'}
          </button>
        ))}
      </div>

      {tab === 'factures' && (
        <div className="card overflow-x-auto">
          {documents.length === 0 ? (
            <p className="text-sm text-gray-500">Aucun document d'achat.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Numéro</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-right">TTC</th>
                  <th className="px-3 py-2 text-right">Reste</th>
                  <th className="px-3 py-2 text-center">Statut</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {documents.map((d) => (
                  <tr key={d.id} className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/documents/view/${d.id}`)}>
                    <td className="px-3 py-2 font-medium text-primary-700">{d.numero}</td>
                    <td className="px-3 py-2 text-gray-600">{d.typeDocument}</td>
                    <td className="px-3 py-2 text-gray-600">{formatDate(d.dateDocument)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatTND(d.totalTTC)}</td>
                    <td className="px-3 py-2 text-right font-mono">{formatTND(d.resteAPayer)}</td>
                    <td className="px-3 py-2 text-center">
                      {d.typeDocument === 'FACTURE' && <StatusBadge statut={d.statutPaiement} />}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {d.typeDocument === 'FACTURE' && d.resteAPayer > 0 && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/paiements/new?documentId=${d.id}`); }}
                          className="inline-flex items-center text-xs text-indigo-600 hover:underline">
                          <BanknotesIcon className="h-4 w-4 mr-0.5" /> Régler
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'catalogue' && (
        <div className="card overflow-x-auto">
          {catalogue.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aucun article lié à ce fournisseur. Ajoutez-les depuis la fiche article (section « Fournisseurs »).
            </p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="px-3 py-2 text-left">Article</th>
                  <th className="px-3 py-2 text-left">Réf. fournisseur</th>
                  <th className="px-3 py-2 text-right">Prix achat</th>
                  <th className="px-3 py-2 text-right">Délai (j)</th>
                  <th className="px-3 py-2 text-center">Préféré</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {catalogue.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/articles/${c.articleId}/edit`)}>
                    <td className="px-3 py-2">
                      <span className="font-medium text-gray-900">{c.articleDesignation}</span>
                      {c.articleReference && <span className="text-xs text-gray-500"> ({c.articleReference})</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{c.referenceFournisseur || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.prixAchat != null ? formatTND(c.prixAchat) : '—'}</td>
                    <td className="px-3 py-2 text-right">{c.delaiJours ?? '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {c.prefere && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-semibold">★</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default FournisseurWorkspace;
